import {
  type CreateOrganizationInput,
  type Organization,
  type OrgMembership,
  organizationSchema,
  orgMembershipSchema,
} from '@noconflict/contracts';
import type { Supabase } from '../../shared/api/supabase';

/** Every organization the caller belongs to (RLS), by name. Soft-deleted ones are never returned. */
export async function listOrganizations(client: Supabase): Promise<Organization[]> {
  const { data, error } = await client
    .from('organizations')
    .select('*')
    .order('name', { ascending: true });
  if (error) throw new Error(error.message);
  return organizationSchema.array().parse(data);
}

/** The caller's own membership rows: their role in each org. */
export async function listOwnMemberships(
  client: Supabase,
  userId: string,
): Promise<OrgMembership[]> {
  const { data, error } = await client.from('org_memberships').select('*').eq('user_id', userId);
  if (error) throw new Error(error.message);
  return orgMembershipSchema.array().parse(data);
}

/** Self-serve org creation (Q13): the caller becomes owner and gets a "General" workspace. */
export async function createOrganization(
  client: Supabase,
  input: CreateOrganizationInput,
): Promise<Organization> {
  const { data, error } = await client.rpc('create_organization', {
    p_name: input.name,
    p_country: input.country,
  });
  if (error) throw new Error(error.message);
  return organizationSchema.parse(data);
}

/** Needs `org.manage`. The slug is stable: only the name changes. */
export async function updateOrganization(
  client: Supabase,
  organizationId: string,
  name: string,
): Promise<void> {
  const { error } = await client.rpc('update_organization', {
    p_organization_id: organizationId,
    p_name: name,
  });
  if (error) throw new Error(error.message);
}

/** Owner only. A soft delete: the org disappears from every list at once. */
export async function deleteOrganization(client: Supabase, organizationId: string): Promise<void> {
  const { error } = await client.rpc('delete_organization', { p_organization_id: organizationId });
  if (error) throw new Error(error.message);
}
